import { Routes, Route, Navigate } from 'react-router-dom';
import { ClerkFailed, SignedIn, SignedOut, SignIn, SignUp } from '@clerk/clerk-react';
import { AppShell } from './shell/AppShell.js';
import { BrandMark } from './shell/BrandMark.js';
import { Button } from './components/ui/button';
import { clerkDarkAppearance } from './lib/clerk-appearance.js';
import { MeProvider } from './context/MeContext.js';
import { CourseHomePage } from './pages/CourseHomePage.js';
import { HoleDetailPage } from './pages/HoleDetailPage.js';
import { CourseMapPage } from './pages/CourseMapPage.js';
import { ZoneDetailPage } from './pages/ZoneDetailPage.js';
import { CoursePickerPage } from './pages/CoursePickerPage.js';
import { LandingPage } from './pages/LandingPage.js';
import { SatellitePage } from './pages/SatellitePage.js';
import { WeatherPage } from './pages/WeatherPage.js';
import { DiseaseRiskPage } from './pages/DiseaseRiskPage.js';
import { BriefingPage } from './pages/BriefingPage.js';
import { PhotoAIPage } from './pages/PhotoAIPage.js';
import { SensorsPage } from './pages/SensorsPage.js';
import { ChemicalPage } from './pages/ChemicalPage.js';
import { TankMixCalculatorPage } from './pages/TankMixCalculatorPage.js';
import { ProductImportPage } from './pages/ProductImportPage.js';
import { IrrigationPage } from './pages/IrrigationPage.js';
import { WaterQualityPage } from './pages/WaterQualityPage.js';
import { AdvisorPage } from './pages/AdvisorPage.js';
import { OwnershipPage } from './pages/OwnershipPage.js';
import { CompliancePage } from './pages/CompliancePage.js';
import { DigitalTwinPage } from './pages/DigitalTwinPage.js';
import { InventoryPage } from './pages/InventoryPage.js';
import { SettingsPage } from './pages/SettingsPage.js';

export function App() {
  return (
    <Routes>
      {/* Unauthenticated marketing + auth surfaces */}
      <Route
        path="/"
        element={
          <>
            {/* Clerk failed to load: the marketing page needs no Clerk, so show it.
                Its Sign in link lands on the explanation below. */}
            <ClerkFailed>
              <LandingPage />
            </ClerkFailed>
            <SignedOut>
              <LandingPage />
            </SignedOut>
            <SignedIn>
              <Navigate to="/app" replace />
            </SignedIn>
          </>
        }
      />
      <Route
        path="/sign-in/*"
        element={
          <AuthFrame>
            <SignInFailed />
            <SignIn
              routing="path"
              path="/sign-in"
              fallbackRedirectUrl="/app"
              appearance={clerkDarkAppearance}
            />
          </AuthFrame>
        }
      />
      <Route
        path="/sign-up/*"
        element={
          <AuthFrame>
            <SignInFailed />
            <SignUp
              routing="path"
              path="/sign-up"
              fallbackRedirectUrl="/app"
              appearance={clerkDarkAppearance}
            />
          </AuthFrame>
        }
      />

      {/* Authenticated app */}
      <Route
        path="/app/*"
        element={
          <>
            <ClerkFailed>
              <AuthFrame>
                <SignInFailed />
              </AuthFrame>
            </ClerkFailed>
            <SignedOut>
              <Navigate to="/sign-in" replace />
            </SignedOut>
            <SignedIn>
              <MeProvider>
                <AppShell>
                  <Routes>
                    <Route index element={<CoursePickerPage />} />
                    <Route path=":courseSlug" element={<CourseHomePage />} />
                    <Route path=":courseSlug/map" element={<CourseMapPage />} />
                    <Route path=":courseSlug/satellite" element={<SatellitePage />} />
                    <Route path=":courseSlug/weather" element={<WeatherPage />} />
                    <Route path=":courseSlug/disease" element={<DiseaseRiskPage />} />
                    <Route path=":courseSlug/briefing" element={<BriefingPage />} />
                    <Route path=":courseSlug/photo" element={<PhotoAIPage />} />
                    <Route path=":courseSlug/holes/:holeNumber" element={<HoleDetailPage />} />
                    <Route path=":courseSlug/zones/:zoneId" element={<ZoneDetailPage />} />
                    <Route path=":courseSlug/sensors" element={<SensorsPage />} />
                    <Route path=":courseSlug/chemical" element={<ChemicalPage />} />
                    <Route path=":courseSlug/chemical/tank-mix" element={<TankMixCalculatorPage />} />
                    <Route path=":courseSlug/chemical/import" element={<ProductImportPage />} />
                    <Route path=":courseSlug/irrigation" element={<IrrigationPage />} />
                    <Route path=":courseSlug/water" element={<WaterQualityPage />} />
                    <Route path=":courseSlug/advisor" element={<AdvisorPage />} />
                    <Route path=":courseSlug/ownership" element={<OwnershipPage />} />
                    <Route path=":courseSlug/compliance" element={<CompliancePage />} />
                    <Route path=":courseSlug/twin" element={<DigitalTwinPage />} />
                    <Route path=":courseSlug/chemical/inventory" element={<InventoryPage />} />
                    <Route path=":courseSlug/settings" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to="/app" replace />} />
                  </Routes>
                </AppShell>
              </MeProvider>
            </SignedIn>
          </>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** The host this device has to reach for sign-in (Clerk's Frontend API), read from the
 *  publishable key: `pk_<env>_<base64 of "host$">`. */
function clerkHost(key: string | undefined): string {
  try {
    return atob(key?.split('_')[2] ?? '').replace(/\$$/, '') || 'the sign-in service';
  } catch {
    return 'the sign-in service';
  }
}

// Clerk's script did not load on this device: a network, firewall or blocker that stops
// the Frontend API. Clerk gives up after its 15 s script timeout and sets status "error";
// <SignIn>, <SignedIn> and <SignedOut> then never render, so without this the page is
// empty. <ClerkFailed> renders only in that state.
function SignInFailed() {
  return (
    <ClerkFailed>
      <div className="w-full max-w-md rounded-lg border border-border bg-bg-card p-8 text-center shadow-lg">
        <BrandMark className="mx-auto h-10 w-10" />
        <h1 className="mt-5 font-display text-2xl font-bold tracking-tight text-fg">
          Sign-in couldn’t load.
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-fg-secondary [overflow-wrap:anywhere]">
          This device couldn’t reach the sign-in service. It’s usually the network: a company
          firewall, a VPN or an ad blocker stopping{' '}
          {clerkHost(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY)}. Reload to try again, or try
          another network.
        </p>
        <Button type="button" className="mt-6" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    </ClerkFailed>
  );
}

function AuthFrame({ children }: { children: import('react').ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-page p-6">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div
          style={{
            position: 'absolute',
            width: 520,
            height: 520,
            top: -160,
            left: -140,
            borderRadius: '50%',
            background: 'var(--green-500)',
            filter: 'blur(140px)',
            opacity: 0.14,
          }}
        />
      </div>
      <div className="relative">{children}</div>
    </div>
  );
}
